import {
  OrganizationType,
  SITE_OPS_ORG_TYPES,
} from "../types/organization.type";
import { orgScopedPermission } from "./jwt";
import {
  PICKUP_ORDER_LIST_ROLES,
  PICKUP_ORDER_WRITE_ROLES,
} from "./legacy-roles";
import type { LegacyRole } from "./types";

export type NavAccessType = "orgScoped";

export interface OpsNavSubItem {
  text: string;
  path: string;
  entity: string;
  action: string;
  legacyRoles?: readonly LegacyRole[];
  /** Narrows the parent group's allowedTypes for this one sub-item; omit to inherit the parent's. */
  allowedTypes?: OrganizationType[];
}

export interface OpsNavItem {
  text: string;
  path: string;
  allowedTypes: OrganizationType[];
  entity: string;
  action: string;
  legacyRoles?: readonly LegacyRole[];
  subItems?: OpsNavSubItem[];
}

const SITE_OPS_ALLOWED = [...SITE_OPS_ORG_TYPES];

/** Ops sidebar catalog — permission + org-type gating. */
export const OPS_NAV_CATALOG: OpsNavItem[] = [
  {
    text: "Suppliers",
    path: "/suppliers",
    allowedTypes: [OrganizationType.RECYCLER, OrganizationType.TRUCK],
    entity: "supplier",
    action: "list",
    subItems: [
      {
        text: "All Suppliers",
        path: "/suppliers",
        entity: "supplier",
        action: "list",
      },
      {
        text: "Collection Points",
        path: "/suppliers/collection-points",
        entity: "supplier",
        action: "list",
      },
    ],
  },
  {
    text: "Monitoring",
    path: "/monitoring/driver-requests",
    // Union of both children's allowedTypes - each child narrows further below.
    // Gated on Driver Requests' own permission (not Orders Monitoring's
    // recycler-only "dashboard.read") so a truck-org user who can't see
    // Orders Monitoring doesn't lose the whole group and, with it, Driver
    // Requests.
    allowedTypes: [OrganizationType.RECYCLER, OrganizationType.TRUCK],
    entity: "route_run",
    action: "read",
    subItems: [
      {
        text: "Orders Monitoring",
        path: "/monitoring/orders",
        entity: "dashboard",
        action: "read",
        allowedTypes: [OrganizationType.RECYCLER],
      },
      {
        text: "Driver Requests",
        path: "/monitoring/driver-requests",
        entity: "assignment",
        action: "list",
      },
      {
        text: "Trips",
        path: "/monitoring/trips",
        entity: "route_run",
        action: "read",
      },
      {
        text: "Order Approvals",
        path: "/monitoring/approvals",
        entity: "order_approval",
        action: "list",
      },
    ],
  },
  {
    text: "Pickup Orders",
    path: "/pickup_orders",
    allowedTypes: SITE_OPS_ALLOWED,
    entity: "pickup_order",
    action: "list",
    legacyRoles: PICKUP_ORDER_LIST_ROLES,
    subItems: [
      {
        text: "All Pickup Orders",
        path: "/pickup_orders",
        entity: "pickup_order",
        action: "list",
        legacyRoles: PICKUP_ORDER_LIST_ROLES,
      },
      {
        text: "Create Pickup Order",
        path: "/pickup_orders/create",
        entity: "pickup_order",
        action: "create",
        legacyRoles: PICKUP_ORDER_WRITE_ROLES,
      },
    ],
  },
  {
    text: "Fleet",
    path: "/transportation/fleet",
    allowedTypes: [OrganizationType.RECYCLER, OrganizationType.TRUCK],
    entity: "truck",
    action: "list",
    subItems: [
      {
        text: "Trucks",
        path: "/transportation/fleet",
        entity: "truck",
        action: "list",
      },
      {
        text: "Drivers",
        path: "/transportation/drivers",
        entity: "driver",
        action: "list",
      },
    ],
  },
  {
    text: "Route Planning",
    path: "/suppliers/zones",
    allowedTypes: [OrganizationType.RECYCLER, OrganizationType.TRUCK],
    entity: "zone",
    action: "list",
    subItems: [
      {
        text: "Labels",
        path: "/suppliers/zones",
        entity: "zone",
        action: "list",
      },
      {
        text: "Routes",
        path: "/transportation/routes",
        entity: "route",
        action: "list",
      },
      {
        text: "Facilities",
        path: "/transportation/facilities",
        entity: "facility",
        action: "list",
      },
    ],
  },
  {
    text: "Recycling",
    path: "/recycling/materials",
    allowedTypes: SITE_OPS_ALLOWED,
    entity: "material_type",
    action: "list",
    subItems: [
      {
        text: "Material Types",
        path: "/recycling/materials",
        entity: "material_type",
        action: "list",
      },
    ],
  },
  {
    text: "Containers",
    path: "/containers/list",
    allowedTypes: SITE_OPS_ALLOWED,
    entity: "container",
    action: "list",
    subItems: [
      {
        text: "Container List",
        path: "/containers/list",
        entity: "container",
        action: "list",
      },
      {
        text: "Collection Sites",
        path: "/containers/sites",
        entity: "collection_site",
        action: "list",
      },
    ],
  },
];

export function resolveOpsNavPermission(
  entity: string,
  action: string,
  orgId: string | null,
): string | null {
  if (!orgId) {
    return null;
  }
  return orgScopedPermission(orgId, entity, action);
}

export const PERM_USER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "user", "list");
export const PERM_USER_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "user", "write");
export const PERM_ROLE_ASSIGN = (orgId: string) =>
  orgScopedPermission(orgId, "role", "assign");
export const PERM_USER_DELETE = "recyclee.admin.user.delete";

export const PERM_PICKUP_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "list");
export const PERM_PICKUP_CREATE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "create");
export const PERM_PICKUP_WRITE = (orgId: string) =>
  orgScopedPermission(orgId, "pickup_order", "write");

export const PERM_TRUCK_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "truck", "list");
export const PERM_DRIVER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "driver", "list");
export const PERM_MATERIAL_TYPE_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "material_type", "list");
export const PERM_CONTAINER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "container", "list");
export const PERM_COLLECTION_SITE_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "collection_site", "list");
export const PERM_SUPPLIER_LIST = (orgId: string) =>
  orgScopedPermission(orgId, "supplier", "list");
